import clsx from 'clsx'
import { FC, ReactNode } from 'react'
import { BasicStyledComponent, DSBest } from 'shared-definitions/types'
import RWButton from '../buttons/RWButton'
import { AppEventsContext } from '../contexts/AppEventsContext'
import SoundIcon from '../service/SoundIcon'
import AppImage from './AppImage'
import BestBadge from './BestBadge'
import styles from './BestListCard.module.css'
import { useScrollableLinksContainer } from '../hooks/use-scrollbale-links-container'
import Linked from '../service/Linked'

interface BestListCardProps extends BasicStyledComponent {
  card: DSBest
  /** Rendered under the buttons, the card's last row. SoundGuys' best-list guides seat the
   *  pick's action row here (Pin to compare · Measurements · Owners, 2026-10-17). */
  footerSlot?: ReactNode
}

const BestListCard: FC<BestListCardProps> = ({ card, className, footerSlot }) => {
  const ref = useScrollableLinksContainer<HTMLDivElement>()
  const { appEvent } = AppEventsContext.useContainer()

  const imageContent = card.image && (
    <>
      <AppImage layout="fill" image={card.image} sizes={[{ bp: 'md', val: 300 }, 150]} />
      {card.badge ? (
        <AppImage
          image={card.badge}
          imageClassName={styles.badgeImage}
          className={styles.badge}
          sizes={[{ bp: 'md', val: 48 }, 25]}
          layout="raw"
        />
      ) : null}
    </>
  )

  return (
    <div className={clsx(styles.container, className)} ref={ref}>
      {card.shopLink ? (
        <Linked className={styles.imageContainer} {...card.shopLink}>
          {imageContent}
        </Linked>
      ) : (
        <div className={styles.imageContainer}>{imageContent}</div>
      )}
      <div className={styles.descriptionContainer}>
        {card.shopLink ? (
          <Linked {...card.shopLink} className={styles.title}>
            {card.title}
          </Linked>
        ) : (
          <div className={styles.title}>{card.title}</div>
        )}
        {card.headLabel && (
          <BestBadge
            className={styles.tag}
            title={card.headLabel.title}
            featured={card.isFeatured}
            iconEl={
              card.headLabel.icon ? (
                <SoundIcon icon={card.headLabel.icon} className={styles.tagIcon} />
              ) : null
            }
          />
        )}
      </div>
      <div className={styles.buttonsContainer}>
        {card.shopLink && (
          <RWButton
            className={styles.shopButton}
            variant="secondary"
            size="large"
            pLink={card.shopLink}
            onClick={() => {
              appEvent({
                event: 'widget-interaction',
                widgetType: 'Clearbuy Best List Topper',
                label: 'Check price',
                url: card.shopLink?.href ?? '',
              })
            }}
          >
            {'Check price'}
          </RWButton>
        )}
        {card.reviewButton ? (
          <RWButton
            variant="tertiary"
            size="large"
            className={styles.reviewLink}
            pLink={card.reviewButton.pLink}
            onClick={() => {
              appEvent({
                event: 'widget-interaction',
                widgetType: 'Clearbuy Best List Topper',
                label: card.reviewButton?.pLink.href ?? '',
                url: card.reviewButton?.label ?? '',
              })
            }}
          >
            {card.reviewButton.label}
          </RWButton>
        ) : null}
      </div>
      {footerSlot ? <div className={styles.footer}>{footerSlot}</div> : null}
    </div>
  )
}

export default BestListCard
